import { differenceInCalendarDays, format, parseISO, isValid } from "date-fns";

/** Formats a number with up to 2 decimals, trimming trailing zeros. */
export function formatNumber(value: number, maxDecimals = 2): string {
  if (Number.isNaN(value)) return "0";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: maxDecimals,
  }).format(value);
}

/** Formats a whole number with thousands separators. */
export function formatInt(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}

/** Quotes a CSV cell and neutralizes spreadsheet formula prefixes in untrusted values. */
export function formatCsvCell(value: unknown): string {
  const text = value == null ? "" : String(value);
  const safe = /^[\s]*[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

/** Clamps a percentage to a 0-100 range for progress widths. */
export function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/** Parses an ISO date/date-time string safely. */
function toDate(value: string | Date): Date | null {
  if (value instanceof Date) return value;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/** "Sep 1, 2026" */
export function formatDate(value: string | Date): string {
  const date = toDate(value);
  return date ? format(date, "MMM d, yyyy") : "—";
}

/** "Sep 1" (no year) */
export function formatDateShort(value: string | Date): string {
  const date = toDate(value);
  return date ? format(date, "MMM d") : "—";
}

/** "Sep 1, 2026 · 3:04 PM" */
export function formatDateTime(value: string | Date): string {
  const date = toDate(value);
  return date ? format(date, "MMM d, yyyy · h:mm a") : "—";
}

/**
 * "Aug 1 – Sep 25, 2026" when both ends share a year, otherwise
 * "Dec 1, 2026 – Jan 5, 2027". Keeps date ranges on one line in narrow
 * columns instead of wrapping a lone "2026" onto its own row.
 */
export function formatDateRange(
  from: string | Date,
  to: string | Date,
): string {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return "—";
  return start.getFullYear() === end.getFullYear()
    ? `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`
    : `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
}

/** Relative-ish label: Today / Yesterday / date. */
export function formatRelativeDay(value: string | Date): string {
  const date = toDate(value);
  if (!date) return "—";
  const today = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (startOf(today).getTime() - startOf(date).getTime()) / 86_400_000,
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  return formatDate(date);
}

/** Formats a yyyy-MM-dd key from a Date (local, no timezone shift). */
export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Parses a yyyy-MM-dd string into a local Date (midnight). */
export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Whole days between two yyyy-MM-dd keys (negative when `to` is in the past). */
export function daysBetweenKeys(from: string, to: string): number {
  const utcDay = (key: string) => {
    const [year, month, day] = key.split("-").map(Number);
    return Date.UTC(year, (month ?? 1) - 1, day ?? 1) / 86_400_000;
  };
  return utcDay(to) - utcDay(from);
}

/** Inclusive number of calendar days in a date range. */
export function inclusiveDaysBetween(
  from: string | Date,
  to: string | Date,
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 1;
  return Math.max(1, differenceInCalendarDays(end, start) + 1);
}

/** Human countdown such as "today", "tomorrow", "in 5 days", "3 weeks". */
export function formatDayCount(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 14) return `in ${days} days`;
  if (days < 60) return `in ${Math.round(days / 7)} weeks`;
  return `in ${Math.round(days / 30)} months`;
}

/** Initials for avatar fallbacks. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}
